import React, { FC } from 'react';
import {
  Button,
  Col,
  FormFeedback,
  FormGroup,
  FormText,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { BaseUser } from '../../../../shared/user/base';
import { ScannedUserInformation } from '../../../../shared/user/cashier';
import { getDate } from '../../../components/Date';
import { JSONForm } from '../../../components/json-form/JSONForm';
import { useTranslation } from '../../../translations';
import { useUserSafe } from '../../../utils';

interface ProfileEditModalProps {
  readonly user: BaseUser;
  readonly isMinor?: boolean;
  readonly showAddress?: boolean;
  readonly scannedUserInformation?: ScannedUserInformation;
  readonly complete: () => void;
  readonly close: () => void;
}

export const ProfileEditModal: FC<ProfileEditModalProps> = ({
  user,
  complete,
  close,
  isMinor,
  showAddress,
  scannedUserInformation,
}) => {
  const { ts } = useTranslation();
  const localUser = useUserSafe();

  return (
    <Modal className="modal-large" id="profileCard" isOpen>
      <JSONForm method="patch" onSuccess={complete} path={`/api/users/${user.id}`}>
        <ModalHeader>{ts('user.edit')}</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg={6} md={12}>
              <FormGroup>
                <Label>{ts('common.preferred_name')}</Label>
                <Input defaultValue={user.preferredName ?? ''} name="preferredName" />
              </FormGroup>
            </Col>
            <Col lg={6} md={12}>
              <FormGroup>
                <Label for="bornAt">{ts('common.date_of_birth')}</Label>
                <Input
                  defaultValue={user.bornAt ? getDate(new Date(user.bornAt)) : ''}
                  disabled={localUser.id === user.id}
                  invalid={isMinor}
                  name="bornAt"
                  pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}"
                  placeholder="YYYY-MM-DD"
                  style={{ minWidth: '150px ' }}
                  type="date"
                />
                {isMinor && <FormFeedback>{ts('attendee_is_a_minor_check')}</FormFeedback>}
              </FormGroup>
            </Col>
            <Col lg={6} md={12}>
              <FormGroup>
                <Label for="firstName">{ts('common.first_name')}</Label>
                <Input
                  defaultValue={scannedUserInformation?.firstName ?? user.firstName ?? ''}
                  name="firstName"
                />
                {scannedUserInformation?.firstName && (
                  <FormText color="muted">
                    ({ts('user.edit_updated_from')} {user.firstName})
                  </FormText>
                )}
              </FormGroup>
            </Col>
            <Col lg={6} md={12}>
              <FormGroup>
                <Label for="lastName">{ts('common.last_name')}</Label>
                <Input
                  defaultValue={scannedUserInformation?.lastName ?? user.lastName ?? ''}
                  name="lastName"
                />
                {scannedUserInformation?.firstName && (
                  <FormText color="muted">
                    ({ts('user.edit_updated_from')} {user.lastName})
                  </FormText>
                )}
              </FormGroup>
            </Col>
          </Row>
          {showAddress && <UserAddressInformation user={user} />}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" id="saveProfile" type="submit">
            {ts('user.update')}
          </Button>
          <Button color="secondary" onClick={close}>
            {ts('common.cancel')}
          </Button>
        </ModalFooter>
      </JSONForm>
    </Modal>
  );
};

const UserAddressInformation: FC<{ readonly user: BaseUser }> = ({ user }) => {
  const { ts } = useTranslation();
  return (
    <Row>
      <Col lg={6} md={12}>
        <FormGroup>
          <Label for="email">{ts('common.email')}</Label>
          <Input defaultValue={user.email ?? ''} name="email" />
        </FormGroup>
      </Col>
      <Col lg={6} md={12}>
        <FormGroup>
          <Label for="phone">{ts('phone')}</Label>
          {/* TODO: This COULD be null? */}
          <Input defaultValue={user.phone!} name="phone" />
        </FormGroup>
      </Col>
      <Col lg={6} md={12}>
        <FormGroup>
          <Label for="addressLine1">{ts('common.address_line_1')}</Label>
          <Input defaultValue={user.address?.line1} name="addressLine1" />
        </FormGroup>
      </Col>
      <Col lg={6} md={12}>
        <FormGroup>
          <Label for="addressLine2">{ts('common.address_line_2_optional')}</Label>
          <Input defaultValue={user.address?.line2 ?? ''} name="addressLine2" />
        </FormGroup>
      </Col>
      <Col lg={6} md={12}>
        <FormGroup>
          <Label for="addressCity">{ts('common.city')}</Label>
          <Input defaultValue={user.address?.city} name="addressCity" />
        </FormGroup>
      </Col>
      <Col lg={6} md={12}>
        <FormGroup>
          <Label for="addressState">{ts('common.state_province')}</Label>
          <Input defaultValue={user.address?.state} name="addressState" />
        </FormGroup>
      </Col>
      <Col lg={6} md={12}>
        <FormGroup>
          <Label for="addressZipcode">{ts('common.zip_postal_code')}</Label>
          <Input defaultValue={user.address?.zipcode} name="addressZipcode" />
        </FormGroup>
      </Col>
      <Col lg={6} md={12}>
        <FormGroup>
          <Label for="addressCountry">{ts('common.country')}</Label>
          <Input defaultValue={user.address?.country} name="addressCountry" />
        </FormGroup>
      </Col>
    </Row>
  );
};
